import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiCheckCircleOutline, mdiCloudUploadOutline } from '@mdi/js';
import Icon from '../../../../../../components/envago/Icon/Icon';
import Information, { InformationDetails, InformationType } from '../../../../../../components/envago/Information/Information';
import Card from '../../../../../../components/envago/Card/Card';
import LabeledList from '../../../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../../../components/envago/OptionalLabel/OptionalLabel';
import { shortenObis } from '../../../../../../helper/MeterHelper';
import { splitJoin } from '../../../../../../helper/TextHelper';
import { formatDate } from '../../../../../../helper/DateHelper';
import CommunicationJobDetailsSection from '../../../../../common/Communication/CommunicationJobDetailsSection';
import JobMeterRateTaskReadingImages, { MeterReadingPreviewImage } from '../JobMeterRateTaskReadingImages';
import ReadingValueLabel from '../ReadingValueLabel/ReadingValueLabel';
import { JobType } from '../../../../../../redux/jobs/jobs.types';
import { MeterReadingConfigType } from '../../../../../../redux/application/application.types';
import { buildSubmittedReadingRows, SubmittedReadingSnapshot } from './JobSuccessScreen.helper';

interface JobSuccessScreenProps {
    job: JobType;
    readingResultImages: Array<MeterReadingPreviewImage>;
    meterReading: MeterReadingConfigType;
    features: { showReadingImagesAllowed?: boolean; showReadingCommentAllowed?: boolean; showPlausibilityAsText?: boolean };
    isPending?: boolean;
    hasOtherOpenJobs?: boolean;
    submittedReading?: SubmittedReadingSnapshot;
}

const JobSuccessScreen = ({ job, readingResultImages, meterReading, features, isPending, hasOtherOpenJobs, submittedReading }: JobSuccessScreenProps) => {
    const { t } = useTranslation();
    const [commVisible, setCommVisible] = useState(false);
    const readingRows = buildSubmittedReadingRows({ meterRateTasks: job.meter.meterRateTasks, submittedReading });

    const headerConfig = isPending
        ? { bg: 'bg-blue-50 border-blue-200', icon: mdiCloudUploadOutline, iconColor: 'text-blue-500', titleKey: 'jobs.details.pendingSuccessTitle' }
        : { bg: 'bg-green-50 border-green-200', icon: mdiCheckCircleOutline, iconColor: 'text-green-500', titleKey: 'jobs.details.successTitle' };

    return (
        <div>
            <Card className={'text-sm'}>
                <div role="status" aria-label={t(headerConfig.titleKey)} className={`${headerConfig.bg} border rounded py-8 px-4 mb-3 flex flex-col items-center`}>
                    <Icon path={headerConfig.icon} className={`w-20 h-20 ${headerConfig.iconColor}`} />
                    <h3 className={'text-xl font-semibold text-center text-gray-800 mt-3'}>{t(headerConfig.titleKey)}</h3>
                    <p className={'text-sm text-center text-gray-600 mt-2'}>
                        {isPending ? (
                            <>
                                {t('jobs.details.pendingSuccessHint')}
                                {hasOtherOpenJobs ? ` ${t('jobs.details.pendingSuccessHintOtherJobs')}` : ''}
                            </>
                        ) : hasOtherOpenJobs ? (
                            t('jobs.details.successDescriptionOtherJobs')
                        ) : (
                            <>
                                {t('jobs.details.successDescriptionAllDone')}
                                <br />
                                {t('jobs.details.successDescriptionThankYou')}
                            </>
                        )}
                    </p>
                </div>
                {/* Always mounted (not conditionally rendered) so onVisibilityChange fires before first paint */}
                <Information type={InformationType.INFO} className={`mb-3 ${commVisible ? '' : 'hidden'}`}>
                    <InformationDetails title={t('communication.jobDetails.title')}>
                        <CommunicationJobDetailsSection onVisibilityChange={setCommVisible} standalone />
                    </InformationDetails>
                </Information>
                {readingRows.map((mr) => (
                    <Card key={mr.taskId} aria={{ 'aria-label': `Ablesetarif ${mr.rateName || mr.obis}`, role: 'group' }}>
                        <LabeledList>
                            <OptionalLabel label={t('jobs.details.labels.obis')}>
                                {mr.rateName ? splitJoin(' ', mr.rateName, mr.obis && `(${shortenObis(mr.obis)})`) : mr.obis && `${shortenObis(mr.obis)}`}
                            </OptionalLabel>
                            <OptionalLabel label={t('jobs.details.labels.readingDate')}>
                                {formatDate(mr.readingDate)}
                            </OptionalLabel>
                            <ReadingValueLabel
                                label={t('jobs.details.labels.readingValue')}
                                value={mr.readingValue}
                                postDecimals={meterReading?.queryPostDecimals ? mr.postDecimals : 0}
                                preDecimals={mr.preDecimals}
                                maskPostDecimals={meterReading?.maskPostDecimals}
                                unit={mr.unit}
                                obis={mr.obis}
                                maximumValue={mr.readingValueValidation?.maximumValue}
                                minimumValue={mr.readingValueValidation?.minimumValue}
                                plausibilityAsText={features?.showPlausibilityAsText}
                            />
                            {features?.showReadingImagesAllowed && readingResultImages && readingResultImages.filter((i) => i.taskId === mr.taskId).length > 0 && (
                                <OptionalLabel label={t('jobs.details.labels.meterRateTaskReadingImages')} labelClassName={'self-start'}>
                                    <JobMeterRateTaskReadingImages taskId={mr.taskId} images={readingResultImages} />
                                </OptionalLabel>
                            )}
                            {features?.showReadingCommentAllowed && mr.comment != null && (
                                <OptionalLabel label={t('jobs.details.labels.meterRateTaskReadingComment')} labelClassName={'self-start'}>
                                    {mr.comment}
                                </OptionalLabel>
                            )}
                        </LabeledList>
                    </Card>
                ))}
            </Card>
        </div>
    );
};

export default JobSuccessScreen;
